import { Pitch } from 'Types';

type Natural = 'C' | 'D' | 'E' | 'F' | 'G' | 'A' | 'B';
type Sharp = 'C#' | 'D#' | 'F#' | 'G#' | 'A#';
type Flat = 'Db' | 'Eb' | 'Gb' | 'Ab' | 'Bb';

export type BaseNote = Natural | Sharp | Flat;
export type Note = `${BaseNote}${number}`;

export function midiNumberToBaseNote(number: number): BaseNote {
  const note = number % 12;
  switch (note) {
    case 0:
      return 'C';
    case 1:
      return 'C#';
    case 2:
      return 'D';
    case 3:
      return 'D#';
    case 4:
      return 'E';
    case 5:
      return 'F';
    case 6:
      return 'F#';
    case 7:
      return 'G';
    case 8:
      return 'G#';
    case 9:
      return 'A';
    case 10:
      return 'A#';
    case 11:
      return 'B';
    default:
      throw 'must be 0 <= number <= 11';
  }
}

export function getNoteNameFromMidiNumber(number: number): Note {
  const octave = number / 12;
  return (midiNumberToBaseNote(number) + (octave - 1).toString()) as Note;
}

export function getMidiNumberFromBaseNote(note: BaseNote): number {
  switch (note) {
    case 'C':
      return 0;
    case 'C#':
    case 'Db':
      return 1;
    case 'D':
      return 2;
    case 'D#':
    case 'Eb':
      return 3;
    case 'E':
      return 4;
    case 'F':
      return 5;
    case 'F#':
    case 'Gb':
      return 6;
    case 'G':
      return 7;
    case 'G#':
    case 'Ab':
      return 8;
    case 'A':
      return 9;
    case 'A#':
    case 'Bb':
      return 10;
    case 'B':
      return 11;
    default:
      throw new Error(`Invalid base note: ${note}`);
  }
}

export function getBaseNote(note: Note): BaseNote {
  const matches = note.match(/([A-G][#, b]?)(-?[0-9])/);
  if (matches != null) {
    const [, note, _] = matches;
    return note as BaseNote;
  } else {
    throw new Error(`Invalid note: ${note}`);
  }
}

export function getOctave(note: Note): number {
  const matches = note.match(/([A-G][#, b]?)(-?[0-9])/);
  if (matches != null) {
    const [, , octave] = matches;
    return Number(octave);
  } else {
    throw new Error(`Invalid note: ${note}`);
  }
}

export function getMidiNumberFromNote(note: Note): number {
  const matches = note.match(/([A-G][#, b]?)(-?[0-9])/);
  if (matches != null) {
    const [, note, octave] = matches;
    return (Number(octave) + 1) * 12 + getMidiNumberFromBaseNote(note as BaseNote);
  } else {
    throw new Error(`Invalid note: ${note}`);
  }
}

export function isNatural(note: Note | number): boolean {
  const base = (typeof note === 'number' ? note : getMidiNumberFromNote(note)) % 12;
  if (base == 1 || base == 3 || base == 6 || base == 8 || base == 10) {
    return false;
  } else {
    return true;
  }
}

export function getMidiNumberFromNoteTuple([note, octave]: [BaseNote, number]) {
  return (octave + 1) * 12 + getMidiNumberFromBaseNote(note);
}

export function getAccidentalShift(accidental: number) {
  switch (accidental) {
    // Sharp
    case 0:
      return 1;
    // Flat
    case 1:
      return -1;
    // None
    case 2:
      return 0;
    default:
      console.error('other accidental shifts not currently supported :(');
      return null;
  }
}

export function getBaseNoteFrom(fundamentalNote: number, accidental: number) {
  const shiftedNote = fundamentalNote + getAccidentalShift(accidental)!;
  switch (shiftedNote) {
    case 0:
      return 'C';
    case 1:
      return 'C#';
    case 2:
      return 'D';
    case 3:
      return 'D#';
    case 4:
      return 'E';
    case 5:
      return 'F';
    case 6:
      return 'F#';
    case 7:
      return 'G';
    case 8:
      return 'G#';
    case 9:
      return 'A';
    case 10:
      return 'A#';
    case 11:
      return 'B';
    default:
      console.error(
        `Note number must lie within the range 0-11 found ${fundamentalNote}, ${accidental}`,
      );
      return null;
  }
}

export function noteFromOsmdPitch(pitch: Pitch): [BaseNote, number] {
  return [getBaseNoteFrom(pitch.fundamentalNote, pitch.accidental)!, pitch.octave + 3];
}
